import pluralize from 'pluralize';
import { type ChangeEvent, type KeyboardEvent, type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';

import { useTrackItemAddedToShowcase, useTrackShareClicked } from '@air/analytics';
import { Skeleton } from '@air/component-skeleton';
import { LinkIcon, PlusIcon, SearchIcon } from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { Input } from '@air/primitive-input';
import { useAirModal } from '@air/provider-modal';
import { CustomToastItem, useToasts } from '@air/provider-toast';
import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { CreateShowcaseModal } from '~/components/ShowcaseModal/CreateShowcaseModal';
import { ShowcaseActionConfirmationToastContent } from '~/components/ShowcaseModal/components/ShowcaseActionConfirmationToastContent';
import { useAddCollectionItemsMutation } from '~/components/ShowcaseModal/hooks/mutations/useAddCollectionItemsMutation';
import { useShowcaseCreatedToast } from '~/components/ShowcaseModal/hooks/useShowcaseCreatedToast';
import { SHOWCASE_MENU_LIMIT } from '~/constants/showcaseMenuLimit';
import { useNavigateToShowcaseEdit } from '~/hooks/useNavigateToShowcaseEdit';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useListShortUrls } from '~/swr-hooks/shortUrl/useListShortUrls';
import { isCollectionOwner, shouldShowExistingShowcasesMenuSection } from '~/utils/permissions/shortUrlPermissions';
import { canCreateCollection, canEditCollection } from '~/utils/permissions/workspacePermissions';
import { getFullShareUrl } from '~/utils/shareUrl';

export type GetShowcaseMenuOptionsParams = {
  assetIds: string[];
  boardIds: string[];
};

const SHOWCASE_SEARCH_DEBOUNCE_MS = 200;

export const useGetPrivateShowcaseMenuOptions = () => {
  const dispatch = useDispatch();
  const { data: account } = useAccountContext();
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, SHOWCASE_SEARCH_DEBOUNCE_MS, {
    trailing: true,
  });
  const [showCreateShowcaseModal, closeCreateShowcaseModal] = useAirModal(CreateShowcaseModal);
  const { showShowcaseCreatedToast } = useShowcaseCreatedToast({ closeCreateShowcaseModal });
  const { mutate: addCollectionItems } = useAddCollectionItemsMutation();
  const { showToast } = useToasts();
  const { navigateToShowcaseEdit } = useNavigateToShowcaseEdit();
  const { trackShareClicked } = useTrackShareClicked();
  const { trackItemAddedToShowcase } = useTrackItemAddedToShowcase();

  const { data: shortUrlsData, isFetching } = useListShortUrls({
    workspaceId: currentWorkspace?.id,
    limit: SHOWCASE_MENU_LIMIT,
    orderBy: {
      field: 'createdAt',
      direction: 'desc',
    },
    search: debouncedSearch || undefined,
  });
  const canEditAllShowcases = canEditCollection(workspacePermissions);
  const editableShortUrls = useMemo(
    () =>
      (shortUrlsData?.data ?? []).filter(
        (shortUrl) => canEditAllShowcases || isCollectionOwner(shortUrl.collection, account?.id),
      ),
    [shortUrlsData?.data, canEditAllShowcases, account?.id],
  );

  const onSearchInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  // Required to allow the search input to be focused when clicking on the search input when displayed in a drop-down menu
  // in *some* contexts, such as when triggered from Table View.
  const stopPropagation = useCallback((e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  const onAddToShowcase = useCallback(
    (shortUrlId: string, collectionId: string, { assetIds, boardIds }: GetShowcaseMenuOptionsParams) => {
      if (currentWorkspace?.id) {
        // No `file_count`: this path carries ids only, and files ride the wire inside `assetIds`
        // (the API has no `fileIds`), so the asset/file split isn't knowable here. `asset_count`
        // therefore includes any files — accepted, since the occurrence matters more than the split.
        trackItemAddedToShowcase({ asset_count: assetIds.length, board_count: boardIds.length });

        addCollectionItems(
          { workspaceId: currentWorkspace.id, collectionId, assetIds, boardIds },
          {
            onSuccess: () => {
              const totalItems = assetIds.length + boardIds.length;
              const shareUrl = getFullShareUrl(shortUrlId);

              showToast(
                ({ onDismiss }) => (
                  <ShowcaseActionConfirmationToastContent
                    message={`${pluralize('item', totalItems, true)} added to showcase`}
                    shareUrl={shareUrl}
                    onDismiss={onDismiss}
                    // Product-confirmed: the add-items toast links to the edit surface too.
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onEdit={() => navigateToShowcaseEdit(collectionId)}
                  />
                ),
                {
                  timeBeforeAutoDismiss: 5_000,
                  CustomContainer: CustomToastItem,
                },
              );

              dispatch(resetSelectedItemsAction());
            },
          },
        );
      }
    },
    [addCollectionItems, currentWorkspace?.id, dispatch, navigateToShowcaseEdit, showToast, trackItemAddedToShowcase],
  );

  const searchShowcasesOptions = useMemo<DropdownMenuOption[]>(
    () => [
      {
        id: 'share-link-search',
        type: 'children',
        children: (
          <div className="flex flex-col">
            <div className="p-2 font-bold ads:text-metadata text-10 text-gray-10 uppercase tracking-[0.4px]">
              Add to existing showcase
            </div>
            <div className="relative">
              <SearchIcon
                size="sm"
                className="absolute top-1/2 left-2 -translate-y-1/2 ads:text-icon-subtle text-gray-9"
              />
              <Input
                className="w-full rounded-8 pr-2 pl-7"
                placeholder={shortUrlsData?.total ? `Search ${shortUrlsData.total} showcases` : 'Search showcases'}
                value={search}
                onChange={onSearchInputChange}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                onKeyDown={stopPropagation}
                size="large"
              />
            </div>
          </div>
        ),
      },
      {
        id: 'share-link-search-separator',
        type: 'separator',
      },
    ],
    [shortUrlsData?.total, search, onSearchInputChange, stopPropagation],
  );

  const getExistingShowcasesOptions = useCallback(
    ({ assetIds, boardIds }: GetShowcaseMenuOptionsParams): DropdownMenuOption[] => {
      const options: DropdownMenuOption[] = [];

      if (isFetching) {
        for (let i = 0; i < SHOWCASE_MENU_LIMIT; i++) {
          options.push({
            id: `skeleton-${i}`,
            label: <Skeleton className="h-4" height={16} width={120} />,
            disabled: true,
            type: 'item',
            prefix: <Skeleton className="size-4" height={16} width={16} />,
          });
        }
      } else if (editableShortUrls.length > 0) {
        for (const shortUrl of editableShortUrls) {
          const { collection } = shortUrl;
          options.push({
            id: collection.id,
            label: collection.name,
            onSelect: () => {
              onAddToShowcase(shortUrl.id, collection.id, { assetIds, boardIds });
            },
            type: 'item',
            prefix: <LinkIcon size="sm" className="ads:text-icon-default text-gray-10" />,
          });
        }
      } else if (debouncedSearch.trim().length > 0) {
        options.push({
          id: 'no-search-results',
          label: 'No search results',
          disabled: true,
          type: 'item',
        });
      } else if ((shortUrlsData?.data?.length ?? 0) === 0) {
        options.push({
          id: 'no-share-links',
          label: 'No showcases',
          disabled: true,
          type: 'item',
        });
      } else if (editableShortUrls.length === 0) {
        options.push({
          id: 'search-for-share-links',
          label: 'Search for your showcases',
          disabled: true,
          type: 'item',
        });
      }

      options.push({
        id: 'new-share-link-separator',
        type: 'separator',
      });

      return options;
    },
    [editableShortUrls, onAddToShowcase, debouncedSearch, isFetching, shortUrlsData?.data?.length],
  );

  const getNewShowcaseOptions = useCallback(
    ({ assetIds, boardIds }: GetShowcaseMenuOptionsParams): DropdownMenuOption[] => [
      {
        id: 'new-share-link',
        label: 'New showcase',
        prefix: <PlusIcon size="sm" className="ads:text-icon-default text-gray-10" />,
        onSelect: () => {
          trackShareClicked({ location: 'context_menu' });
          showCreateShowcaseModal({ assetIds, boardIds, onShowcaseCreated: showShowcaseCreatedToast });
        },
        type: 'item',
      },
    ],
    [showCreateShowcaseModal, showShowcaseCreatedToast, trackShareClicked],
  );

  const getShowcaseMenuOptions = useCallback(
    ({ assetIds, boardIds }: GetShowcaseMenuOptionsParams): DropdownMenuOption[] => {
      const options: DropdownMenuOption[] = [];
      const showExistingShowcasesSection = shouldShowExistingShowcasesMenuSection({
        workspacePermissions,
        editableShortUrlsCount: editableShortUrls.length,
        isFetching,
        debouncedSearch,
      });

      if (showExistingShowcasesSection) {
        options.push(...searchShowcasesOptions);
        options.push(...getExistingShowcasesOptions({ assetIds, boardIds }));
      }

      if (canCreateCollection(workspacePermissions)) {
        options.push(...getNewShowcaseOptions({ assetIds, boardIds }));
      }

      return options;
    },
    [
      debouncedSearch,
      editableShortUrls.length,
      getExistingShowcasesOptions,
      getNewShowcaseOptions,
      isFetching,
      searchShowcasesOptions,
      workspacePermissions,
    ],
  );

  return {
    editableShortUrls,
    getShowcaseMenuOptions,
    isFetching,
    onAddToShowcase,
  };
};
